import { replaceS3Domains, unwrapAssetUrl } from 'src/app/core/util/transform';
import { genEmptyOssltModules as genBaseOssltModules, OSSLT_MODULE_DEFS } from './dummy-session-data';
import {
  derivedMmLoc2,
  mmLocToCssPercent,
  resolveMmLoc2ForDisplay,
  resolveMmLocForDisplay,
} from './osslt-map-modules';
import { IAssessmentFrameworkDetail } from 'src/app/ui-item-maker/item-set-editor/models/assessment-framework';

/**
 * Builds the student OSSLT map modules from a constructed test form + framework.
 * Shared by the live student journey and the in-editor Module Map preview so
 * authoring can feed a panel without going through the anonymous sample API.
 */
export function buildOssltJourneyModules(opts: {
  asmtFmrk: any;
  testForm: { currentTestDesign?: any; questionSrcDb?: Map<any, any> | any };
  sectionsAllowed: number[];
  lang: string;
  getTranslation?: (slug: string, _unused?: any, params?: any) => string;
  getQuestion?: (id: number) => any;
}): any[] {
  const { asmtFmrk, testForm, sectionsAllowed, lang } = opts;
  const modules: any[] = genBaseOssltModules(sectionsAllowed);
  if (!asmtFmrk?.partitions || !testForm) {
    return modules;
  }

  const getQuestion = (id: number) => {
    if (opts.getQuestion) {
      const fromCaller = opts.getQuestion(+id);
      if (fromCaller) {
        return fromCaller;
      }
    }
    const db = testForm.questionSrcDb;
    if (!db) {
      return null;
    }
    if (typeof db.get === 'function') {
      return db.get(+id) || db.get(`${id}`);
    }
    return db[+id] || db[id];
  };

  /**
   * Meta params reach us either as a plain value or as a serialized authoring element (a text ref
   * carrying a caption, or an image element). A serialized one has to be parsed before it is read.
   */
  const parseMetaRef = (ref: any) => {
    if (typeof ref !== 'string') {
      return ref;
    }
    const firstChar = ref.trim().charAt(0);
    if (firstChar !== '{' && firstChar !== '[') {
      return ref;
    }
    try {
      return JSON.parse(ref);
    } catch {
      return ref;
    }
  };
  const getLongText = (ref: any) => {
    const val = parseMetaRef(ref);
    return typeof val === 'string' ? val : val ? val.caption : undefined;
  };
  const getTextUrl = (ref: any) => {
    const val = parseMetaRef(ref);
    return val && typeof val !== 'string' ? val.voice_url : undefined;
  };
  const getMediaUrl = (ref: any) => {
    const val = parseMetaRef(ref);
    // image elements keep the url at the top level, with the hi-contrast variant carrying its own
    const raw =
      unwrapAssetUrl(val) ||
      (val && typeof val === 'object'
        ? unwrapAssetUrl(val.urls?.[0]) || unwrapAssetUrl(val.hiContrastImg)
        : undefined);
    return raw ? replaceS3Domains(raw) : undefined;
  };
  const asImageElement = (ref: any) => {
    if (!ref) {
      return null;
    }
    if (typeof ref === 'string') {
      const trimmed = ref.trim();
      if (trimmed.startsWith('{')) {
        try {
          return JSON.parse(trimmed);
        } catch {
          return { url: trimmed };
        }
      }
      return { url: trimmed };
    }
    return ref;
  };

  const applyModuleParams = (target: any, question: any, partition: any) => {
    if (!question?.meta) {
      return;
    }
    const params = question.meta;
    const locParams = {
      MM_LOC: parseMetaRef(params.MM_LOC),
      MM_LOC2: parseMetaRef(params.MM_LOC2),
    };
    const pinLoc = resolveMmLocForDisplay(locParams.MM_LOC, {
      applyLegacyMmLocOverride: true,
    });
    const blurbLocation =
      resolveMmLoc2ForDisplay(locParams, { applyLegacyMmLocOverride: true }) ||
      (pinLoc ? derivedMmLoc2(pinLoc) : null);

    const txtLocation = getLongText(params.MM_CITY);
    const locationImage = asImageElement(params.MM_TIMG);
    const locationImageAltText = locationImage && locationImage.altText;
    if (locationImageAltText) {
      target.alt = locationImageAltText;
    }

    target.location = txtLocation;
    target.location_slug = txtLocation;
    target.title = partition.description;
    const titleDef = OSSLT_MODULE_DEFS[partition.description];
    target.title_slug = titleDef?.[lang]?.title_slug;
    const genericBadge = opts.getTranslation
      ? opts.getTranslation('lbl_earned_generic', undefined, {
          LOCATION: txtLocation,
        })
      : txtLocation;
    target.earnedBadge_slug = getLongText(params.MM_BCON) || genericBadge;
    target.earnedBadge_voice =
      getMediaUrl(params.MM_BCONV) || getTextUrl(params.MM_BCON);
    target.region = txtLocation;
    target.popUpDesc = getLongText(params.MM_TXT);
    target.popUpImg = getMediaUrl(params.MM_TIMG);
    target.badge = getMediaUrl(params.MM_BIMG);

    target.MM_CITYV = getMediaUrl(params.MM_CITYV);
    target.MM_TXTV = getMediaUrl(params.MM_TXTV);
    target.MM_TIMGV = getMediaUrl(params.MM_TIMGV);

    if (pinLoc) {
      const pinCss = mmLocToCssPercent(pinLoc);
      target.bottom = pinCss.bottom;
      target.right = pinCss.right;
    }
    if (blurbLocation) {
      const blurbCss = mmLocToCssPercent(blurbLocation);
      target.popUpBottom = blurbCss.bottom;
      target.popUpRight = blurbCss.right;
    }
  };

  const sections = testForm.currentTestDesign?.sections || [];
  for (let i = 0; i < sectionsAllowed.length; i++) {
    const section_i = sectionsAllowed[i];
    const sectionDef = asmtFmrk.partitions[section_i];
    const module = modules[i];
    if (!sectionDef || !module) {
      continue;
    }
    module.noReadingSelection = true;
    const section = sections[section_i] || { questions: [] };
    if (sectionDef.mapMetaItemId) {
      applyModuleParams(
        module,
        getQuestion(+sectionDef.mapMetaItemId),
        sectionDef
      );
    }
    (section.questions || []).forEach((questionId: any) => {
      const question = getQuestion(+questionId);
      if (!question) {
        return;
      }
      if (!sectionDef.mapMetaItemId && question.meta?.MM_TXT) {
        applyModuleParams(module, question, sectionDef);
      }
      if (question.isReadingSelection) {
        module.noReadingSelection = false;
      }
    });
  }

  return modules;
}

export function checkHasMapModule(
  sectionIndex: number,
  asmtFmrk: IAssessmentFrameworkDetail,
  testForm: any
): boolean {
  const sectionDef = asmtFmrk?.partitions?.[sectionIndex];
  const section = testForm?.currentTestDesign?.sections?.[sectionIndex];
  
  if (!sectionDef || !section) { return false; }
  if (!OSSLT_MODULE_DEFS[sectionDef.description]) { return false; }
  if (sectionDef.mapMetaItemId) { return true; }

  return (section.questions || []).some((questionId: string | number) => {
    const question = testForm?.questionSrcDb?.get(+questionId);
    return !!question?.meta?.MM_TXT;
  });
}